import { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Btn } from './index';
import Icon from './Icon';
import { formatDisplayDate } from '../utils/dateCalendar';
import { planningOrderEditReason, planningUndoReason } from '../utils/planningOrderActions';
import './planning-workspace.css';

export default function PlanningOrders({ programs, partial = false, busy = false, compact = false, onEdit, onUndo, onDelete, children }) {
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const showDetails = !compact || expanded;
  const undoReason = planningUndoReason(programs, partial);
  const editReasons = programs.map(program => planningOrderEditReason(program, partial));
  const sharedReason = editReasons.length && editReasons[0] && editReasons.every(reason => reason === editReasons[0]) ? editReasons[0] : '';
  const separateUndoReason = undoReason && undoReason !== sharedReason;
  const list = <ul className="planning-order-list" aria-label="Pedidos do planejamento">
    {programs.map(program => {
      const editReason = planningOrderEditReason(program, partial);
      const count = Number(program.total_passageiros ?? program.passageiros?.length ?? 0);
      return <li key={program.id}>
        <div className="planning-order-info">
          <strong>Pedido {program.codigo}</strong>
          <p>{!compact && <>{formatDisplayDate(program.data)} · </>}{String(program.horario || '').slice(0, 5)} · {!compact && <>{program.trecho === 'ida' ? 'Ida' : 'Volta'} · </>}{count} {count === 1 ? 'passageiro no pedido' : 'passageiros no pedido'}</p>
          {editReason && !sharedReason && <p id={`${id}-edit-${program.id}`} className="planning-order-restriction">{editReason}</p>}
        </div>
        <div className="planning-order-actions">
          <Btn size="sm" variant="secondary" disabled={busy || Boolean(editReason)}
            aria-label={`Editar pedido ${program.codigo}`}
            aria-describedby={editReason ? sharedReason ? `${id}-shared` : `${id}-edit-${program.id}` : undefined}
            onClick={() => onEdit(program)}><Icon name="edit" size={16} /> Editar pedido</Btn>
          {onDelete && <Btn size="sm" variant="secondary" disabled={busy || Boolean(editReason)}
            aria-label={`Excluir pedido ${program.codigo}`}
            aria-describedby={editReason ? sharedReason ? `${id}-shared` : `${id}-edit-${program.id}` : undefined}
            onClick={() => onDelete(program)}>Excluir pedido</Btn>}
        </div>
      </li>;
    })}
  </ul>;

  return <section className={`planning-orders${compact ? ` planning-orders-compact${programs.length === 1 ? ' planning-orders-single' : ''}` : ''}`} aria-label="Editar pedidos e desfazer planejamento">
    <div className="planning-orders-heading">
      <div><h3>{programs.length === 1 ? "Pedido de origem" : "Pedidos de origem"}</h3>{!compact && <p>Altere data, trecho e horário no pedido. Para a lista de uma van, use Editar passageiros.</p>}</div>
      {compact && <Btn size="sm" variant="secondary" aria-expanded={expanded} aria-controls={`${id}-details`} onClick={() => setExpanded(value => !value)}>
        <Icon name="chevron-down" style={{transform:expanded ? 'rotate(180deg)' : undefined}} />
        {expanded ? 'Recolher pedidos' : `Ver e editar pedidos (${programs.length})`}
      </Btn>}
      {!compact && onUndo && <Btn size="sm" variant="secondary" disabled={busy || !programs.length || Boolean(undoReason)}
        aria-describedby={undoReason ? separateUndoReason ? `${id}-undo` : `${id}-shared` : undefined}
        onClick={() => onUndo(programs[0])}><Icon name="back" size={16} /> Desfazer planejamento</Btn>}
      {children}
    </div>
    <div id={`${id}-details`} hidden={!showDetails}>
      {showDetails && <>
        {compact && <div className="planning-orders-detail-actions"><p>Altere data, trecho e horário no pedido de origem.</p>
          {onUndo && <Btn size="sm" variant="secondary" disabled={busy || !programs.length || Boolean(undoReason)}
            aria-describedby={undoReason ? separateUndoReason ? `${id}-undo` : `${id}-shared` : undefined}
            onClick={() => onUndo(programs[0])}><Icon name="back" size={16} /> Desfazer planejamento</Btn>}
        </div>}
        {sharedReason && <p className="planning-order-restriction" id={`${id}-shared`}>{sharedReason}</p>}
        {onUndo && separateUndoReason && <p className="planning-order-restriction" id={`${id}-undo`}>{undoReason}</p>}
        {list}
      </>}
    </div>
  </section>;
}

PlanningOrders.propTypes = {
  programs: PropTypes.array.isRequired, partial: PropTypes.bool, busy: PropTypes.bool, compact: PropTypes.bool,
  onEdit: PropTypes.func.isRequired, onUndo: PropTypes.func, onDelete: PropTypes.func, children: PropTypes.node,
};
